import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { ContactMenuIcon, FileMenuIcon, LocationMenuIcon, MicMenuIcon, TimeMenuIcon } from './icons';
import { ICON_BTN } from './styles';
import styles from './AttachMenu.module.css';
import { IconButton } from '../ui';

// The composer's "+" attachment menu: a small popover anchored on the plus button with the
// attachment options — "Choose file" (the file picker), "Contact" (the contact picker),
// "Location" (the place picker), and "Share a time" (the datetime picker). When no menu is
// needed the caller renders the bare button instead — this component is only used once there's
// more than one attach option.
export interface AttachMenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
}

export function AttachMenu({ triggerLabel, items, triggerClassName }: { triggerLabel: string; items: AttachMenuItem[]; triggerClassName?: string }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={styles.root}>
      <IconButton
        size="small"
        onClick={() => setOpen((v) => !v)}
        aria-label={triggerLabel}
        title={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        className={triggerClassName}
      >
        {/* Figma "Prompt Box Buttons" (14705:128186): every composer glyph is a 1.5px absolute stroke —
            this trigger used to carry the default relative 2.5, reading visibly heavier than its
            siblings. */}
        <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </IconButton>
      {open && (
        <div role="menu" className={styles.menu}>
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              <span className={styles.itemIcon} aria-hidden>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Re-export the option glyphs so the composer can hand them in without reaching into ./icons.
export { ContactMenuIcon, FileMenuIcon, LocationMenuIcon, MicMenuIcon, TimeMenuIcon };
